import { SubmitPayOverTimeManualPaymentUnionTypenames } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useSubmitPayOverTimeManualPayment from 'features/payOverTime/screens/payments/hooks/useSubmitPayOverTimeManualPayment';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const PayOverTimeConfirmPayment = () => {
  const router = useRouter();
  const { t } = useTranslation('payOverTime', { keyPrefix: 'manualRepay' });
  const { bottom } = useSafeAreaInsets();
  const { repayAmount, accountName, paymentSchedule, loanId, sourceAccountId } =
    useLocalSearchParams<{
      repayAmount: string;
      accountName: string;
      paymentSchedule?: string;
      loanId: string;
      sourceAccountId: string;
    }>();

  const {
    executeMutationSubmitPayOverTimeManualPayment,
    fetching,
    errorModel,
  } = useSubmitPayOverTimeManualPayment();

  useViewDidFocusScreenAnalyticsEvent(
    errorModel
      ? {
          key: 'payOverTimeManualPaymentErrorEncountered',
          customAttributes: { error_name: 'submit_failed' },
        }
      : null,
  );

  useEffect(() => {
    if (!repayAmount && !accountName) {
      router.push('/pay-over-time/loan-details');
    }
  }, [repayAmount, accountName, router]);

  const handleConfirm = async () => {
    const result = await executeMutationSubmitPayOverTimeManualPayment({
      amount: {
        amount: repayAmount,
        currency: 'USD',
      },
      loanId: loanId,
      fromAccountId: sourceAccountId,
    });

    if (
      result &&
      result.__typename ===
        SubmitPayOverTimeManualPaymentUnionTypenames.PayOverTimeSubmitManualPaymentSuccess
    ) {
      router.replace({
        pathname: '/pay-over-time/manual-payment/success',
        params: {
          captionText: result.paymentMessage.captionText,
          titleText: result.paymentMessage.headlineText,
          bodyText: result.paymentMessage.messageText,
          infoMessage: result.paymentMessage.noteText,
          isPaidOff: String(Number(result.outstandingBalance.amount) === 0),
        },
      });
    }
  };

  const handleHardwareBackPress = () => {
    if (fetching) {
      return;
    }
    router.back();
  };

  useBackHandler(handleHardwareBackPress);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          gestureEnabled: !fetching,
          title: t('confirmPayment'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} isPrimaryDisabled={fetching} />
          ),
        }}
      />
      <ScrollView>
        <View style={styles.innerContainer}>
          <ListItem
            primaryCopy={'$' + repayAmount}
            lineFormat={ListItemLineFormat.LABEL}
            secondaryCopy={t('paymentAmount')}
            showsChevron={false}
          />
          <View style={styles.hairLine}>
            <HairLineDivider />
          </View>
          <ListItem
            primaryCopy={accountName ?? ''}
            secondaryCopy={t('repayFrom')}
            lineFormat={ListItemLineFormat.LABEL}
            showsChevron={false}
          />
          {paymentSchedule && paymentSchedule?.length > 1 ? (
            <>
              <View style={styles.hairLine}>
                <HairLineDivider />
              </View>
              <ListItem
                primaryCopy={paymentSchedule ?? ''}
                secondaryCopy={t('paymentSchedule')}
                lineFormat={ListItemLineFormat.LABEL}
                showsChevron={false}
              />
            </>
          ) : null}
        </View>
      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          label={t('confirmPayment')}
          type="PRIMARY"
          width="FULL"
          size="LARGE"
          onPress={handleConfirm}
          isLoading={fetching}
          isDisabled={fetching || !!errorModel}
        />
      </View>
      {errorModel ? (
        <ScreenTransitionOverlay
          isLoading={false}
          LoadingComponent={undefined}
          ErrorComponent={
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={errorModel?.messageHeadline ?? ''}
              messageBody={errorModel?.messageBody ?? ''}
              insetBottomSafeArea={false}
              primaryButton={{
                title: t('buttons.done'),
                onPress: () => router.back(),
              }}
            />
          }
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  hairLine: {
    marginHorizontal: theme.spacing.m,
  },
  innerContainer: {
    flex: 1,
  },
});

export default PayOverTimeConfirmPayment;
